import {  Category, COLLECTIONS } from '@bookstore/shared';
import { collection, query, where, orderBy, getDocs, getDoc, doc, limit } from 'firebase/firestore';
import { getFirebaseFirestore } from '@bookstore/shared/browser';

export class CategoryService {
  private get db() {
    return getFirebaseFirestore();
  }


  /**
   * Get all active categories
   */
  async getCategories(catLimit?: number): Promise<Category[]> {
    try {
      const categoriesQuery = query(
        collection(this.db, COLLECTIONS.CATEGORIES),
        where('isActive', '==', true),
        orderBy('order', 'asc'),
        ...(catLimit ? [limit(catLimit)] : [])
      );

      const snapshot = await getDocs(categoriesQuery);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Category[];
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * Get category by ID
   */
  async getCategoryById(categoryId: string): Promise<Category | null> {
    try {
      const categoryDoc = await getDoc(doc(this.db, COLLECTIONS.CATEGORIES, categoryId));

      if (!categoryDoc.exists()) {
        return null;
      }

      return { id: categoryDoc.id, ...categoryDoc.data() } as Category;
    } catch (error) {
      console.error('Error fetching category:', error);
      throw error;
    }
  }

  /**
   * Get category by SEO URL slug
   */
  async getCategoryBySlug(slug: string): Promise<Category | null> {
    try {
      const categoriesQuery = query(
        collection(this.db, COLLECTIONS.CATEGORIES),
        where('seoUrl', '==', slug),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(categoriesQuery);

      if (snapshot.empty) {
        return null;
      }

      const categoryDoc = snapshot.docs[0];
      return { id: categoryDoc.id, ...categoryDoc.data() } as Category;
    } catch (error) {
      console.error('Error fetching category by slug:', error);
      throw error;
    }
  }

  /**
   * Get parent categories (top-level)
   */
  async getParentCategories(): Promise<Category[]> {
    try {
      const categoriesQuery = query(
        collection(this.db, COLLECTIONS.CATEGORIES),
        where('isActive', '==', true),
        where('parentId', '==', null),
        orderBy('order', 'asc')
      );

      const snapshot = await getDocs(categoriesQuery);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Category[];
    } catch (error) {
      console.error('Error fetching parent categories:', error);
      throw error;
    }
  }

  /**
   * Get subcategories for a parent
   */
  async getSubcategories(parentId: string): Promise<Category[]> {
    try {
      const categoriesQuery = query(
        collection(this.db, COLLECTIONS.CATEGORIES),
        where('isActive', '==', true),
        where('parentId', '==', parentId),
        orderBy('order', 'asc')
      );

      const snapshot = await getDocs(categoriesQuery);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Category[];
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      throw error;
    }
  }

  async getBreadcrumbs(categoryId: string): Promise<Category[]> {
    const breadcrumbs: Category[] = [];
    let currentId: string | null = categoryId;

    while (currentId) {
      const category = await this.getCategoryById(currentId);
      if (!category) break;
      breadcrumbs.unshift(category);
      currentId = category.parentId || null;
    }

    return breadcrumbs;
  }
}

// Export singleton instance
export const categoryService = new CategoryService();
